import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { tokenStorage, decodeJwt } from '@/utils/jwt';
import { AuthUser, authService, isTwoFactorChallenge, TwoFactorChallenge } from '@/services/auth.service';
import { settingsService, MeProfile } from '@/services/settings.service';

interface AuthCtx {
  user: AuthUser | null;
  profile: MeProfile | null;
  /** Resolves with a challenge when the account asks for a second step; otherwise the session is open. */
  loginWithPassword: (email: string, password: string) => Promise<TwoFactorChallenge | null>;
  completeTwoFactor: (challengeId: string, code: string) => Promise<{ backupCodesRemaining?: number }>;
  logout: () => void;
}

export const AuthContext = createContext<AuthCtx | null>(null);

/**
 * Read the JWT from localStorage and decode it into an AuthUser. Returns null
 * if the token is missing, malformed, or expired (and clears it in the latter
 * two cases). Pulled out of the effect so it can also seed `useState`'s lazy
 * initializer — without that, the first render after a hard refresh runs with
 * `user = null` and ProtectedRoute redirects to /login before useEffect ever
 * fires, even when the stored token is perfectly valid.
 */
function rehydrateUser(): AuthUser | null {
  const t = tokenStorage.get();
  if (!t) return null;
  const p = decodeJwt(t);
  if (!p || p.exp * 1000 < Date.now()) {
    tokenStorage.clear();
    return null;
  }
  return { userUid: p.sub, role: p.role, companyUid: p.companyUid };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(rehydrateUser);
  const [profile, setProfile] = useState<MeProfile | null>(null);

  // Load profile only when we have a non-super-admin company user.
  useEffect(() => {
    if (!user) { setProfile(null); return; }
    if (user.role === 'super_admin') { setProfile(null); return; }
    let alive = true;
    settingsService.me().then((p) => { if (alive) setProfile(p); }).catch(() => undefined);
    return () => { alive = false; };
  }, [user]);

  const onAuthSuccess = useCallback((token: string, u: AuthUser) => {
    tokenStorage.set(token);
    setUser(u);
  }, []);

  const value = useMemo<AuthCtx>(() => ({
    user,
    profile,
    loginWithPassword: async (email, password) => {
      const r = await authService.loginWithPassword(email, password);
      if (isTwoFactorChallenge(r)) return r;
      onAuthSuccess(r.token, r.user);
      return null;
    },
    completeTwoFactor: async (challengeId, code) => {
      const r = await authService.verifyTwoFactor(challengeId, code);
      onAuthSuccess(r.token, r.user);
      return { backupCodesRemaining: r.backupCodesRemaining };
    },
    logout: () => { tokenStorage.clear(); setUser(null); setProfile(null); },
  }), [user, profile, onAuthSuccess]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
